import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { stockyApi } from '../misc/StockyApi';

const OrderSummaryContext = createContext({
  summary: { unreadCount: 0 },
  loading: false,
  refreshSummary: async () => null,
});

const SUMMARY_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const SUMMARY_REFRESH_JITTER_MS = 30 * 1000;

const getNextRefreshDelay = () => {
  const jitter = (Math.random() * 2 - 1) * SUMMARY_REFRESH_JITTER_MS;
  return Math.round(SUMMARY_REFRESH_INTERVAL_MS + jitter);
};

function OrderSummaryProvider({ children }) {
  const { getUser, userIsAuthenticated } = useAuth();
  const currentUser = getUser();
  const accessToken = currentUser?.accessToken || '';
  const authUser = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);
  const authenticated = Boolean(currentUser?.accessToken) && userIsAuthenticated();
  const [summary, setSummary] = useState({ unreadCount: 0 });
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshSummary = useCallback(async ({ silent = false } = {}) => {
    if (!authUser || !authenticated) {
      if (mountedRef.current) {
        setSummary({ unreadCount: 0 });
      }
      return null;
    }

    if (!silent && mountedRef.current) {
      setLoading(true);
    }

    try {
      const response = await stockyApi.getOrderSummary(authUser);
      const nextSummary = {
        unreadCount: Number(response?.data?.unreadCount || 0),
      };
      if (mountedRef.current) {
        setSummary(nextSummary);
      }
      return nextSummary;
    } catch (error) {
      if (mountedRef.current) {
        setSummary({ unreadCount: 0 });
      }
      return null;
    } finally {
      if (mountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [authUser, authenticated]);

  useEffect(() => {
    if (!authUser || !authenticated) {
      setSummary({ unreadCount: 0 });
      return undefined;
    }

    let cancelled = false;
    let refreshTimeoutId;

    const scheduleNextRefresh = () => {
      refreshTimeoutId = window.setTimeout(async () => {
        await refreshSummary({ silent: true });
        if (!cancelled) {
          scheduleNextRefresh();
        }
      }, getNextRefreshDelay());
    };

    refreshSummary();
    scheduleNextRefresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSummary({ silent: true });
      }
    };

    window.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimeoutId);
      window.removeEventListener('focus', handleVisibilityChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authUser, authenticated, refreshSummary]);

  const contextValue = useMemo(
    () => ({
      summary,
      loading,
      refreshSummary,
    }),
    [summary, loading, refreshSummary],
  );

  return (
    <OrderSummaryContext.Provider value={contextValue}>
      {children}
    </OrderSummaryContext.Provider>
  );
}

function useOrderSummary() {
  return useContext(OrderSummaryContext);
}

export {
  OrderSummaryProvider,
  useOrderSummary,
};
